import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RotateCcw, LayoutDashboard, Copy, Check } from 'lucide-react';
import { reportClientError } from '@/lib/reportError';

/* The SPA's safety net. A render that throws would otherwise unmount the whole
   tree and leave a blank white page, which tells the person nothing and tells
   us less. React only lets a class component catch that, so this one stays a
   class while everything around it is hooks.

   Two variants: `page` fills the screen and is the last resort in App.jsx;
   `content` sits inside Layout around the outlet, so the navigation survives a
   crashed page and the person can simply click somewhere else. */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error, copied: false };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Unhandled error in the Westwood Project Hub UI:', error, errorInfo);
    this.setState({ componentStack: errorInfo?.componentStack });
    /* Tell us as well, so a crash is not left resting on whether the person
       thinks to copy the details block and send them on. Never awaited: the
       report must not delay showing them the message. */
    reportClientError(error, errorInfo);
  }

  reset = () => {
    this.setState({ error: null, componentStack: undefined, copied: false });
  };

  details() {
    const { error, componentStack } = this.state;
    return [
      `Page: ${window.location.pathname}${window.location.search}`,
      `Time: ${new Date().toISOString()}`,
      `Error: ${error?.message || String(error)}`,
      error?.stack ? `\nStack:\n${error.stack}` : '',
      componentStack ? `\nComponent stack:${componentStack}` : '',
    ].join('\n');
  }

  copy = async () => {
    try {
      await navigator.clipboard.writeText(this.details());
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      /* Clipboard access can be refused; the text is on screen to select. */
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const fullPage = this.props.variant === 'page';

    return (
      <div
        className={
          fullPage
            ? 'flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-white to-slate-50 p-6'
            : 'flex flex-col items-center justify-center py-16 px-6'
        }
      >
        <div className="max-w-xl w-full p-8 bg-white rounded-lg shadow-lg border border-slate-100">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-orange-100">
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Something went wrong</h1>
            <p className="text-slate-600 mb-8">
              This part of the Project Hub ran into an error and could not finish loading.
              Nothing you were looking at has been changed. Try again, and if it keeps
              happening send the details below to the app administrator.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <Button onClick={this.reset}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Try again
              </Button>
              <Button variant="outline" onClick={() => { window.location.href = '/Dashboard'; }}>
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>

            <details className="text-left bg-slate-50 rounded-md border border-slate-200">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-slate-700">
                Technical details
              </summary>
              <div className="px-4 pb-4 space-y-3">
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-slate-600">
                  {this.details()}
                </pre>
                <Button variant="outline" size="sm" onClick={this.copy}>
                  {this.state.copied
                    ? <><Check className="w-4 h-4 mr-2" />Copied</>
                    : <><Copy className="w-4 h-4 mr-2" />Copy details</>}
                </Button>
              </div>
            </details>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
